import React from "react";
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideoCameraFrontIcon from "@mui/icons-material/VideoCameraFront";
import MovieIcon from "@mui/icons-material/Movie";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { COLORS } from "./constants";
import { barePanelSx, previewButtonSx, sectionTitleSx } from "./styles";

// ─────────────────────────────────────────────────────────────────────────────
// The preview pane shows two fundamentally different pictures depending on the
// selected source, and saying which one matters:
//
//   RTMP / SRT -> the SERVER ROUND-TRIP: /live/<userId>.flv pulled back down
//     through flv.js. It cannot exist until an encoder is actually publishing,
//     because node-media-server parks a play request for an unpublished path
//     in idlePlayers and writes nothing at all (node_flv_session.js onPlay) —
//     which the reverse proxy eventually turns into a 504. It also runs a few
//     seconds behind the encoder.
//
//   Browser -> the LOCAL MediaStream, assigned straight to video.srcObject by
//     BrowserEncodePanel. No server involved, no delay, and it works BEFORE
//     you go live — so there the preview is a genuine pre-flight check.
//
// The distinction is not cosmetic: with a browser source a perfect picture
// proves your camera works and proves NOTHING about whether frames are
// reaching viewers, since browserEncodeService still has to pipe webm into
// FFmpeg and republish to the local RTMP ingest. Hence the badge is green only
// for a verified round-trip and cyan for a local preview.
// ─────────────────────────────────────────────────────────────────────────────

const PREVIEW_BY_SOURCE = {
  rtmp: {
    Icon: VideocamIcon,
    title: "Live Preview",
    empty: "Appears once your encoder connects.",
    detail: "Returned from the server, so it runs a few seconds behind what OBS is sending.",
    badge: "~3 s delay",
    badgeColor: COLORS.green
  },
  srt: {
    Icon: VideocamIcon,
    title: "SRT Relay",
    // The relay carries the picture camera -> VPS -> viewer over SRT the whole
    // way and never publishes to the media server, so there is no .flv to pull
    // back down. This pane previously showed the RTMP ingest here, which made
    // an RTMP broadcast look like a working SRT preview.
    empty: "Open your viewer URL in VLC or ffplay to watch this feed.",
    detail: "The relay sends camera to viewer directly, so there is no server copy to preview here.",
    badge: "Direct",
    badgeColor: COLORS.cyan
  },
  browser: {
    Icon: VideoCameraFrontIcon,
    title: "Your Camera",
    empty: "Start the camera in Source to see yourself.",
    detail: "Local preview from this browser — no delay, and visible before you go live.",
    badge: "Local",
    badgeColor: COLORS.cyan
  },
  file: {
    Icon: MovieIcon,
    title: "Scheduled File",
    empty: "The selected MP4 plays at its scheduled time.",
    detail: "This is a recorded file, not a live signal.",
    badge: "File",
    badgeColor: COLORS.muted
  }
};

export function LivePreviewPanel({
  previewUrl, startPreview, stopPreview, videoRef,
  sourceKind = "rtmp", uptimeLabel, fps,
  // Rendered in place of the empty-state text, whatever the source. Home decides
  // what belongs there — the MP4 dropzone, or the host node the browser camera
  // preview is portalled into. Passed in rather than built here so this panel
  // stays a preview surface and knows nothing about uploads or capture.
  emptyStateSlot = null
}) {
  // Resolution comes from the video element itself, not the backend. FFmpeg's
  // progress lines carry fps and bitrate but no frame size, so reporting it
  // server-side would mean parsing the stream-open banner. videoWidth/Height
  // is the size actually being DECODED right now, which is both free and more
  // truthful — it reflects what a viewer's player would see.
  const [resolution, setResolution] = React.useState(null);

  // The native <video controls> bar was showing a SEEK slider and an elapsed
  // counter on a live stream. Neither means anything here — you cannot scrub a
  // broadcast, and "0:05" reads like the length of a recording rather than a
  // feed that started five seconds ago. The picture now fills the frame like a
  // monitor, with only the two controls that do apply, revealed on hover.
  //
  // Muted is NOT just a preference: browsers refuse to autoplay a stream with
  // sound, so the feed has to start silent. Dropping the controls entirely
  // would therefore have left no way to ever hear the audio — which is exactly
  // what an operator checks before going live.
  const [muted, setMuted] = React.useState(true);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const frameRef = React.useRef(null);

  const toggleMute = React.useCallback(() => {
    const el = videoRef?.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
  }, [videoRef]);

  const toggleFullscreen = React.useCallback(() => {
    const box = frameRef.current;
    if (!box) return;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else box.requestFullscreen?.();
  }, []);

  // Mirrors the real state, so pressing Esc still updates the icon.
  React.useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement === frameRef.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  React.useEffect(() => {
    const el = videoRef?.current;
    if (!el || !previewUrl) { setResolution(null); return undefined; }

    const read = () => {
      if (el.videoWidth > 0 && el.videoHeight > 0) {
        setResolution(`${el.videoWidth}×${el.videoHeight}`);
      }
    };
    read();
    // loadedmetadata fires on first decode; resize covers a mid-stream
    // resolution change, which happens when an encoder switches rendition.
    el.addEventListener("loadedmetadata", read);
    el.addEventListener("resize", read);
    return () => {
      el.removeEventListener("loadedmetadata", read);
      el.removeEventListener("resize", read);
    };
  }, [previewUrl, videoRef]);

  const cfg = PREVIEW_BY_SOURCE[sourceKind] || PREVIEW_BY_SOURCE.rtmp;
  const { Icon } = cfg;
  // The browser source renders its own local <video> inside BrowserEncodePanel;
  // pulling the round-trip FLV here as well would be pointless traffic and
  // would reintroduce the 504/retry loop for a stream we already have locally.
  // Only RTMP actually has a server-side copy to pull back down. SRT is
  // listed as a round-trip source no longer: it has no .flv, and treating it
  // as one is exactly what made the SRT tab display the RTMP stream.
  const showsRoundTrip = sourceKind === "rtmp";

  return (
    <Box sx={barePanelSx}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center">
          <Icon sx={{ color: COLORS.cyan }} />
          <Typography sx={sectionTitleSx}>{cfg.title}</Typography>
          <Chip
            label={cfg.badge} size="small"
            sx={{
              height: 20, borderRadius: "8px", fontSize: 9.5, fontWeight: 700,
              bgcolor: "rgba(0,0,0,0.28)", color: cfg.badgeColor,
              border: `1px solid ${cfg.badgeColor}`
            }}
          />
        </Stack>
        {showsRoundTrip && (
          <Stack direction="row" spacing={1}>
            <Tooltip title="Start preview">
              <IconButton onClick={startPreview} aria-label="Start preview" sx={previewButtonSx(COLORS.green)}>
                <PlayArrowIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Stop preview">
              <IconButton onClick={stopPreview} aria-label="Stop preview" sx={previewButtonSx(COLORS.red)}>
                <StopCircleIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>

      <Box
        ref={frameRef}
        sx={{
          mt: 2, position: "relative", aspectRatio: "16/9", bgcolor: "#0B0A1F",
          border: `1px solid ${COLORS.border}`, borderRadius: "12px", overflow: "hidden",
          display: "flex", alignItems: "center", justifyContent: "center",
          // Idle it is just the picture. The controls appear when the pointer is
          // over the frame, and stay reachable by keyboard via :focus-within.
          "&:hover .preview-controls, &:focus-within .preview-controls": { opacity: 1 }
        }}
      >
        {showsRoundTrip && previewUrl ? (
          <>
            <video ref={videoRef} muted autoPlay playsInline
              style={{ width: "100%", height: "100%", objectFit: "contain", background: "#0B0A1F" }} />

            {/* Once there is a key the player is attached and retrying, but
                until the first frame decodes it is just a black box. The
                decoder reporting a size is the signal that frames arrived. */}
            {!resolution && (
              <Stack alignItems="center" justifyContent="center" spacing={1.25} sx={{
                position: "absolute", inset: 0, px: 3, textAlign: "center", pointerEvents: "none"
              }}>
                <Icon sx={{ fontSize: 34, color: COLORS.muted, opacity: 0.6 }} />
                <Typography sx={{ color: COLORS.text, fontSize: 15, fontWeight: 600 }}>
                  Waiting for your encoder…
                </Typography>
                <Typography sx={{ color: COLORS.muted, fontSize: 13, lineHeight: 1.5, maxWidth: 340 }}>
                  Press Start Streaming in OBS. The picture appears here a few seconds later.
                </Typography>
              </Stack>
            )}

            {/* Overlay chips. Each renders only when its value is real:
                uptime comes from the server's stream start time, resolution
                from the decoder, fps from FFmpeg. A chip with nothing behind
                it is worse than no chip. `pointerEvents: none` so they never
                swallow a click meant for the frame beneath them. */}
            {(uptimeLabel || resolution || fps != null) && (
              <Stack
                direction="row" spacing={1}
                sx={{ position: "absolute", top: 10, left: 10, pointerEvents: "none" }}
              >
                {uptimeLabel && (
                  <Chip
                    size="small"
                    icon={<FiberManualRecordIcon sx={{ fontSize: "9px !important", color: `${COLORS.red} !important` }} />}
                    label={uptimeLabel}
                    sx={{
                      height: 24, borderRadius: "8px",
                      bgcolor: "rgba(11,10,31,0.78)", border: `1px solid ${COLORS.border}`,
                      color: COLORS.text,
                      "& .MuiChip-label": { fontFamily: "JetBrains Mono, monospace", fontSize: 11.5 }
                    }}
                  />
                )}
                {(resolution || fps != null) && (
                  <Chip
                    size="small"
                    label={[resolution, fps != null ? `${fps} fps` : null].filter(Boolean).join(" · ")}
                    sx={{
                      height: 24, borderRadius: "8px",
                      bgcolor: "rgba(11,10,31,0.78)", border: `1px solid ${COLORS.border}`,
                      color: "#D9D6EA",
                      "& .MuiChip-label": { fontFamily: "JetBrains Mono, monospace", fontSize: 11.5 }
                    }}
                  />
                )}
              </Stack>
            )}

            <Stack
              className="preview-controls"
              direction="row" spacing={0.5}
              sx={{
                position: "absolute", bottom: 10, right: 10,
                opacity: 0, transition: "opacity .18s ease",
                bgcolor: "rgba(11,10,31,0.78)", border: `1px solid ${COLORS.border}`,
                borderRadius: "10px", p: 0.25
              }}
            >
              <Tooltip title={muted ? "Unmute" : "Mute"}>
                <IconButton
                  size="small" onClick={toggleMute}
                  aria-label={muted ? "Unmute preview" : "Mute preview"}
                  sx={{ color: muted ? COLORS.muted : COLORS.cyan }}
                >
                  {muted ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title={isFullscreen ? "Exit full screen" : "Full screen"}>
                <IconButton
                  size="small" onClick={toggleFullscreen}
                  aria-label={isFullscreen ? "Exit full screen" : "Full screen"}
                  sx={{ color: COLORS.muted, "&:hover": { color: COLORS.text } }}
                >
                  {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Stack>
          </>
        ) : emptyStateSlot ? (
          // Nothing to preview yet, but the frame can do something more useful
          // than apologise: for MP4 it becomes the place you drop the file, for
          // Browser it becomes the camera preview itself.
          <Box sx={{ position: "absolute", inset: sourceKind === "file" ? 12 : 0 }}>
            {emptyStateSlot}
          </Box>
        ) : (
          // A still icon, not a spinner: nothing is loading until the encoder
          // connects, and an endless spinner says otherwise.
          <Stack alignItems="center" spacing={1.25} sx={{ px: 3, textAlign: "center" }}>
            <Icon sx={{ fontSize: 34, color: COLORS.muted, opacity: 0.6 }} />
            <Typography sx={{ color: COLORS.text, fontSize: 15, fontWeight: 600 }}>{cfg.empty}</Typography>
            <Typography sx={{ color: COLORS.muted, fontSize: 13, lineHeight: 1.5, maxWidth: 340 }}>
              {cfg.detail}
            </Typography>
          </Stack>
        )}
      </Box>
    </Box>
  );
}
